import React, { useMemo, useState } from 'react';
import {
    Form, Input, InputNumber, Select, Button, Modal, Row, Col, Tag, Tooltip, Typography, Popconfirm, Empty, Checkbox
} from 'antd';
import {
    PlusOutlined, DeleteOutlined, ArrowUpOutlined, ArrowDownOutlined, WarningOutlined, CheckOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { flagFor, rangeLabel, localizedName } from '../utils/labFlags';
import './VisitLabTestCards.css';

const { Text } = Typography;

// The "Lab Tests" section of the visit form.
//
// The doctor picks a test from the dropdown and presses Add; a card opens with
// that test's boxes and nothing else. The boxes come from the catalog
// (LabTestField rows) - nothing about a CBC or a urine exam is written down in
// here, so a field an admin adds to a panel tonight is on the form tomorrow.
//
// Form shape, under `visitLabTests`:
//   [{ _id?, labTestId, results: { [fieldId]: value }, note }]
// `_id` is set on a card loaded from a saved visit, so the server can tell an
// edit from an addition. utils/labFlags turns this into the API's shape.
//
// Flagging here is a preview: the value is judged against the range as it is
// typed, using the patient's sex when known, and the server judges it again on
// save. Both run the same rules.

const FLAG_ICONS = {
    low: <ArrowDownOutlined />,
    high: <ArrowUpOutlined />,
    abnormal: <WarningOutlined />,
    normal: <CheckOutlined />
};

const FLAG_COLORS = { low: 'blue', high: 'red', abnormal: 'red', normal: 'green' };

const flagText = (t, flag) => ({
    low: t('labFlagLow') || 'Low',
    high: t('labFlagHigh') || 'High',
    abnormal: t('labFlagAbnormal') || 'Abnormal',
    normal: t('labFlagNormal') || 'Normal'
}[flag]);

const idString = (value) => (value && value._id ? String(value._id) : String(value || ''));

// One box. Its own component so it can watch its own value: the flag has to
// update as the doctor types, and a hook cannot run inside a loop.
const LabFieldInput = ({ form, listName, cardName, field, sex, lang }) => {
    const { t } = useTranslation();
    const path = [listName, cardName, 'results', idString(field._id)];
    const value = Form.useWatch(path, form);
    const flag = flagFor(field, value, sex);
    const range = rangeLabel(field, sex);
    const label = localizedName(field, lang);

    const flagIcon = flag && flag !== 'normal' ? (
        <Tooltip title={flagText(t, flag)}>
            <span className={`lab-card-flag lab-card-flag-${flag}`}>{FLAG_ICONS[flag]}</span>
        </Tooltip>
    ) : null;

    let control;
    switch (field.dataType) {
        case 'number':
            control = (
                <InputNumber
                    className="lab-card-number"
                    size="small"
                    controls={false}
                    placeholder={range}
                    addonAfter={field.unit || undefined}
                    prefix={flagIcon}
                    // Whatever precision the lab reports; never round a value.
                    stringMode
                />
            );
            break;
        case 'select':
            control = (
                <Select
                    allowClear
                    size="small"
                    placeholder={range || undefined}
                    options={(field.options || []).map((option) => ({ value: option, label: option }))}
                    suffixIcon={flagIcon || undefined}
                />
            );
            break;
        case 'boolean':
            control = (
                <Select
                    allowClear
                    size="small"
                    placeholder={range || undefined}
                    options={[
                        { value: false, label: t('labNegative') || 'Negative' },
                        { value: true, label: t('labPositive') || 'Positive' }
                    ]}
                    suffixIcon={flagIcon || undefined}
                />
            );
            break;
        case 'text':
        default:
            control = (
                <Input
                    size="small"
                    placeholder={range || undefined}
                    suffix={flagIcon || undefined}
                    addonAfter={field.unit || undefined}
                />
            );
    }

    // A required box only bites once something on the card has been entered:
    // a test ordered and not yet resulted has every box empty, and that is not
    // an error. Mirrors the server's rule.
    const rules = field.isRequired ? [{
        validator: (_, current) => {
            const results = form.getFieldValue([listName, cardName, 'results']) || {};
            const anyFilled = Object.values(results).some(
                (v) => v !== null && typeof v !== 'undefined' && String(v).trim() !== ''
            );
            if (!anyFilled) return Promise.resolve();
            if (current === null || typeof current === 'undefined' || String(current).trim() === '') {
                return Promise.reject(new Error(t('labFieldRequired') || 'Required'));
            }
            return Promise.resolve();
        }
    }] : undefined;

    return (
        <Col xs={12} sm={8} lg={6} className={`lab-card-field${flag ? ` lab-card-field-${flag}` : ''}`}>
            <Form.Item
                name={[cardName, 'results', idString(field._id)]}
                label={(
                    <span className="lab-card-label" title={label}>
                        {label}
                        {field.isRequired && <span className="lab-card-required">*</span>}
                    </span>
                )}
                rules={rules}
                validateTrigger={['onBlur', 'onSubmit']}
            >
                {control}
            </Form.Item>
            {range && field.dataType === 'number' && (
                <Text type="secondary" className="lab-card-range">
                    {range}{field.unit ? ` ${field.unit}` : ''}
                </Text>
            )}
        </Col>
    );
};

// One test on the visit: its boxes, a note, and a way to remove it.
const LabTestCard = ({ form, listName, cardName, byId, sex, lang, onRemove }) => {
    const { t } = useTranslation();
    const labTestId = Form.useWatch([listName, cardName, 'labTestId'], form);
    const test = byId.get(idString(labTestId));

    return (
        <div className="lab-card">
            <div className="lab-card-head">
                <div className="lab-card-title">
                    <Text strong>{test ? localizedName(test, lang) : (t('labTestNotInCatalog') || 'Test no longer in catalog')}</Text>
                    {test && test.category && (
                        <Tag className="lab-card-category">{t(`labCategory.${test.category}`, { defaultValue: test.category })}</Tag>
                    )}
                </div>
                <Popconfirm
                    title={t('removeLabTest') || 'Remove this test?'}
                    onConfirm={onRemove}
                    okText={t('yes')}
                    cancelText={t('no')}
                    okButtonProps={{ danger: true }}
                >
                    <Button type="text" danger size="small" icon={<DeleteOutlined />}>
                        {t('remove') || 'Remove'}
                    </Button>
                </Popconfirm>
            </div>

            {/* The card's identity, carried in the form values. */}
            <Form.Item name={[cardName, 'labTestId']} hidden><Input /></Form.Item>
            <Form.Item name={[cardName, '_id']} hidden><Input /></Form.Item>

            {test ? (
                <Row gutter={[8, 0]}>
                    {(test.fields || []).map((field) => (
                        <LabFieldInput
                            key={idString(field._id)}
                            form={form}
                            listName={listName}
                            cardName={cardName}
                            field={field}
                            sex={sex}
                            lang={lang}
                        />
                    ))}
                </Row>
            ) : (
                <Text type="secondary">{t('labTestNotInCatalogHint') || 'Its saved values are kept; remove the card to drop them.'}</Text>
            )}

            <Form.Item name={[cardName, 'note']} label={t('labNote') || 'Note'} className="lab-card-note">
                <Input.TextArea size="small" autoSize={{ minRows: 1, maxRows: 4 }} placeholder={t('labNotePlaceholder') || 'Sample, lab, remarks…'} />
            </Form.Item>
        </div>
    );
};

const VisitLabTestCards = ({
    form,
    // The active catalog: [{ _id, code, name, translations, category, fields: [...] }]
    catalog = [],
    // 'male' | 'female' | undefined - picks the reference range that applies
    patientSex,
    // [{ key, label }] from GET /api/lab-tests: the English group names the
    // dropdown falls back to when the locale has no `labCategory.<key>`.
    categories = [],
    // Offer "send these to the lab": the cards are also placed as orders on the
    // branch lab's queue when the visit is saved (form field `orderFromLab`).
    allowOrder = false,
    listName = 'visitLabTests'
}) => {
    const { t, i18n } = useTranslation();
    const lang = i18n.language;
    const [selected, setSelected] = useState(null);

    const byId = useMemo(() => new Map(catalog.map((test) => [idString(test._id), test])), [catalog]);
    const categoryLabel = useMemo(() => {
        const english = new Map(categories.map((category) => [category.key, category.label]));
        return (key) => t(`labCategory.${key}`, { defaultValue: english.get(key) || key });
    }, [categories, t]);

    // Grouped by category, matched on every name the test has: a doctor typing
    // "cbc" in the Arabic UI still finds صورة الدم الكاملة.
    const options = useMemo(() => {
        const groups = new Map();
        catalog.forEach((test) => {
            const category = test.category || 'other';
            if (!groups.has(category)) groups.set(category, []);
            groups.get(category).push({
                value: idString(test._id),
                label: localizedName(test, lang),
                // Every name the test answers to, short forms included: "C.B.C"
                // and "cbc" both have to find the CBC.
                searchText: [test.code, test.name, test.translations?.ar, test.translations?.ku, ...(test.aliases || [])]
                    .filter(Boolean).join(' ').toLowerCase().replace(/\./g, '')
            });
        });
        return [...groups.entries()].map(([category, tests]) => ({
            label: categoryLabel(category),
            options: tests
        }));
    }, [catalog, lang, categoryLabel]);

    return (
        <>
        <Form.List name={listName}>
            {(fields, { add, remove }) => {
                const addTest = () => {
                    const test = byId.get(selected);
                    if (!test) return;

                    const doAdd = () => {
                        add({ labTestId: idString(test._id), results: {}, note: '' });
                        setSelected(null);
                    };

                    // The same test twice on one visit is unusual - a repeat sample,
                    // a second lab - so it is asked about rather than refused.
                    const cards = form.getFieldValue(listName) || [];
                    const already = cards.some((card) => card && idString(card.labTestId) === idString(test._id));
                    if (already) {
                        Modal.confirm({
                            title: t('labTestAlreadyAdded') || 'This test is already on the visit',
                            content: t('labTestAlreadyAddedHint') || 'Add it a second time?',
                            okText: t('labAddAgain') || 'Add again',
                            cancelText: t('cancel'),
                            onOk: doAdd
                        });
                        return;
                    }
                    doAdd();
                };

                return (
                    <div className="lab-cards">
                        <div className="lab-cards-picker">
                            <Select
                                showSearch
                                allowClear
                                value={selected}
                                onChange={setSelected}
                                options={options}
                                placeholder={t('selectLabTest') || 'Select a test…'}
                                filterOption={(input, option) =>
                                    // Group headings have no searchText; skip them.
                                    Boolean(option?.searchText) && option.searchText.includes(input.toLowerCase().replace(/\./g, ''))
                                }
                                notFoundContent={catalog.length === 0
                                    ? <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t('labCatalogEmpty') || 'No tests in the catalog'} />
                                    : undefined}
                                className="lab-cards-select"
                                popupMatchSelectWidth={360}
                            />
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={addTest}
                                disabled={!selected}
                            >
                                {t('add') || 'Add'}
                            </Button>
                        </div>

                        {fields.map(({ key, name }) => (
                            <LabTestCard
                                key={key}
                                form={form}
                                listName={listName}
                                cardName={name}
                                byId={byId}
                                sex={patientSex}
                                lang={lang}
                                onRemove={() => remove(name)}
                            />
                        ))}
                    </div>
                );
            }}
        </Form.List>
        {/* Outside the Form.List on purpose: inside it the field name would be
            prefixed with the list's and land in the cards array. */}
        {allowOrder && (
            <Form.Item name="orderFromLab" valuePropName="checked" className="lab-cards-order">
                <Checkbox>
                    {t('sendToLab') || 'Send these tests to the lab'}
                    <Text type="secondary" className="lab-cards-order-hint">
                        {t('sendToLabHint') || 'Each test above is also placed on the lab queue when the visit is saved, so the lab can enter the results.'}
                    </Text>
                </Checkbox>
            </Form.Item>
        )}
        </>
    );
};

export { FLAG_ICONS, FLAG_COLORS, flagText };
export default VisitLabTestCards;
